/**
 * TR sayı biçimi yardımcıları.
 * Görünüm: `85.000,50` — API/kanonik: `85000.50` (nokta ondalık, binlik yok).
 * Hesap yapılmaz; yalnız metin biçimleme.
 */

export type ParsedTrNumber = {
  intDigits: string
  decDigits: string
  /** Kullanıcı virgülü yazmaya başladı mı */
  hasDecimalSep: boolean
}

/** Binlik noktaları ekler: `85000` → `85.000` */
export function addThousandDots(intDigits: string): string {
  if (!intDigits) return ''
  const neg = intDigits.startsWith('-')
  const raw = neg ? intDigits.slice(1) : intDigits
  const withDots = raw.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return neg ? `-${withDots}` : withDots
}

/** Kanonik `85000.50` veya TR `85.000,50` → parçalar */
export function parseNumberInput(raw: string): ParsedTrNumber {
  const cleaned = raw.replace(/[^\d.,-]/g, '')
  if (!cleaned || cleaned === '-' || cleaned === ',' || cleaned === '.') {
    return { intDigits: cleaned === '-' ? '-' : '', decDigits: '', hasDecimalSep: cleaned === ',' || cleaned === '.' }
  }

  const neg = cleaned.startsWith('-')
  const body = neg ? cleaned.slice(1) : cleaned

  // Hem nokta hem virgül: TR varsayımı — son virgül ondalık, noktalar binlik
  if (body.includes(',') && body.includes('.')) {
    const lastComma = body.lastIndexOf(',')
    const intPart = body.slice(0, lastComma).replace(/[^\d]/g, '')
    const decPart = body.slice(lastComma + 1).replace(/[^\d]/g, '')
    return {
      intDigits: neg ? `-${intPart}` : intPart,
      decDigits: decPart,
      hasDecimalSep: true,
    }
  }

  // Yalnız virgül → ondalık ayırıcı
  if (body.includes(',')) {
    const [a = '', b = ''] = body.split(',')
    const intPart = a.replace(/[^\d]/g, '')
    const decPart = b.replace(/[^\d]/g, '')
    return {
      intDigits: neg ? `-${intPart}` : intPart,
      decDigits: decPart,
      hasDecimalSep: true,
    }
  }

  // Yalnız nokta: tek nokta + sonrası ≤2 hane ise ondalık (kanonik API), aksi binlik
  if (body.includes('.')) {
    const parts = body.split('.')
    if (parts.length === 2 && parts[1]!.length > 0 && parts[1]!.length <= 2 && parts[0]!.replace(/[^\d]/g, '').length > 0) {
      // Ambiguous: `85.50` vs `85.000` — if last segment length <= 2 and total dots == 1, treat as decimal
      // But `1.000` has 3 digits after → thousands
      if (parts[1]!.length <= 2 && !/^\d{3}$/.test(parts[1]!)) {
        const intPart = parts[0]!.replace(/[^\d]/g, '')
        const decPart = parts[1]!.replace(/[^\d]/g, '')
        return {
          intDigits: neg ? `-${intPart}` : intPart,
          decDigits: decPart,
          hasDecimalSep: true,
        }
      }
    }
    // Binlik noktaları: tüm noktaları at
    const intPart = body.replace(/[^\d]/g, '')
    return { intDigits: neg ? `-${intPart}` : intPart, decDigits: '', hasDecimalSep: false }
  }

  const intPart = body.replace(/[^\d]/g, '')
  return { intDigits: neg ? `-${intPart}` : intPart, decDigits: '', hasDecimalSep: false }
}

/** Görünen TR metin */
export function formatTrNumberDisplay(
  raw: string,
  opts?: { maxDecimals?: number; minDecimals?: number },
): string {
  const maxDecimals = opts?.maxDecimals ?? 2
  const parsed = parseNumberInput(raw)
  const { intDigits, hasDecimalSep } = parsed
  let { decDigits } = parsed

  if (intDigits === '-' && !decDigits && !hasDecimalSep) return '-'
  if (!intDigits.replace('-', '') && !decDigits && !hasDecimalSep) return ''

  if (decDigits.length > maxDecimals) decDigits = decDigits.slice(0, maxDecimals)

  const intOnly = intDigits.replace('-', '') || (hasDecimalSep || decDigits ? '0' : '')
  const signedInt = intDigits.startsWith('-') ? `-${intOnly}` : intOnly
  const displayInt = addThousandDots(signedInt || (hasDecimalSep ? '0' : ''))

  if (opts?.minDecimals != null && opts.minDecimals > 0 && !hasDecimalSep && !decDigits && intOnly) {
    return `${displayInt},${'0'.repeat(opts.minDecimals)}`
  }

  if (hasDecimalSep || decDigits) {
    return `${displayInt || '0'},${decDigits}`
  }
  return displayInt
}

/** API için kanonik: `85000.50` */
export function toCanonicalNumber(
  raw: string,
  opts?: { maxDecimals?: number },
): string {
  const maxDecimals = opts?.maxDecimals ?? 2
  const { intDigits, decDigits, hasDecimalSep } = parseNumberInput(raw)
  const intOnly = intDigits.replace('-', '')
  if (!intOnly && !decDigits) return ''

  const neg = intDigits.startsWith('-')
  const intPart = intOnly.replace(/^0+(?=\d)/, '') || '0'
  const dec = decDigits.slice(0, maxDecimals)
  if (!hasDecimalSep && !dec) {
    return `${neg ? '-' : ''}${intPart}`
  }
  // trailing zeros optional — keep as typed up to maxDecimals
  return `${neg ? '-' : ''}${intPart}.${dec}`
}

export function isValidCanonicalNumber(raw: string, opts?: { maxDecimals?: number; allowEmpty?: boolean }): boolean {
  if (!raw) return !!opts?.allowEmpty
  const maxDecimals = opts?.maxDecimals ?? 2
  const re = new RegExp(`^-?\\d+(\\.\\d{1,${maxDecimals}})?$`)
  return re.test(raw)
}
